import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { formatToTwoDecimal } from "../../../utils/numberFormatter";

// Dark navy -> light gray, one shade per rank (Top 10).
// Shared by the bar and pie so each party keeps the same color in both.
const PIE_COLORS = [
  "#0B3954",
  "#1D4E6B",
  "#2F5F7C",
  "#46708A",
  "#5E8298",
  "#7894A6",
  "#92A7B5",
  "#ABBAC4",
  "#C3CDD4",
  "#D8DFE4",
];
// Shades from this index on are light enough to need dark label text
const LIGHT_SHADE_START = 5;

const compactNumber = (value) => {
  const abs = Math.abs(value);
  if (abs >= 1e9) return `${+(value / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${+(value / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${+(value / 1e3).toFixed(1)}K`;
  return `${+Number(value).toFixed(0)}`;
};

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const { name, amount_due } = payload[0].payload;
  return (
    <div className="bg-white border shadow-sm px-3 py-2 rounded">
      <div className="fw-semibold mb-1">{name}</div>
      <div className="text-secondary" style={{ fontSize: "0.85rem" }}>
        Amount Due in home currency:{" "}
        <span className="fw-bold text-dark">
          {formatToTwoDecimal(amount_due)}
        </span>
      </div>
    </div>
  );
};

const renderPercentLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  index,
}) => {
  if (percent < 0.03) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill={index >= LIGHT_SHADE_START ? "#3A4A55" : "#FFFFFF"}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={12}
      fontWeight={index < LIGHT_SHADE_START ? 600 : 400}
    >
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

const truncate = (text, max = 22) =>
  text && text.length > max ? `${text.slice(0, max - 1)}…` : text;

const UnpaidBalanceChart = ({ title, data = [], loading = false }) => {
  const hasData = data.length > 0;

  return (
    <div className="p-4 border bg-white" style={{ borderRadius: "1rem" }}>
      <div className="d-flex align-items-baseline flex-wrap gap-2 mb-3">
        <h5 className="fw-bold m-0">{title}</h5>
        {/* <span className="fst-italic text-secondary">in home currency</span> */}
      </div>

      {loading ? (
        <div
          className="d-flex align-items-center justify-content-center text-secondary"
          style={{ height: "18rem" }}
        >
          Loading...
        </div>
      ) : !hasData ? (
        <div
          className="d-flex align-items-center justify-content-center text-secondary"
          style={{ height: "18rem" }}
        >
          No unpaid balances
        </div>
      ) : (
        <div className="row">
          <div className="col-lg-6">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={data}
                layout="vertical"
                margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
              >
                <CartesianGrid horizontal={false} stroke="#EEEEEE" />
                <XAxis
                  type="number"
                  tickFormatter={compactNumber}
                  axisLine={false}
                  tickLine={false}
                  fontSize={12}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={150}
                  tickFormatter={(name) => truncate(name)}
                  tickLine={false}
                  fontSize={12}
                />
                <Tooltip
                  content={<ChartTooltip />}
                  cursor={{ fill: "rgba(0,0,0,0.04)" }}
                />
                <Bar dataKey="amount_due" barSize={22}>
                  {data.map((entry, index) => (
                    <Cell
                      key={entry.id || index}
                      fill={PIE_COLORS[index % PIE_COLORS.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="col-lg-6">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data}
                  dataKey="amount_due"
                  nameKey="name"
                  cx="40%"
                  cy="50%"
                  innerRadius="35%"
                  outerRadius="90%"
                  startAngle={90}
                  endAngle={-270}
                  label={renderPercentLabel}
                  labelLine={false}
                  stroke="#FFFFFF"
                  strokeWidth={1}
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={entry.id || index}
                      fill={PIE_COLORS[index % PIE_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  iconType="circle"
                  iconSize={10}
                  formatter={(value) => (
                    <span className="text-dark" style={{ fontSize: "0.85rem" }}>
                      {truncate(value, 26)}
                    </span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnpaidBalanceChart;
